import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Input,
  useDisclosure,
  Heading,
  IconButton,
  HStack,
  useToast,
  Spinner,
  Center,
  Switch,
  Flex,
  Text,
  Badge,
  VStack,
  Code,
} from '@chakra-ui/react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '@chakra-ui/table';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/modal';
import {
  FormControl,
  FormLabel,
  FormHelperText,
} from '@chakra-ui/form-control';
import { FaEdit, FaTrash, FaPlus, FaKey, FaCopy } from 'react-icons/fa';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const ApiKeys = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedKey, setSelectedKey] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
  });
  const toast = useToast();

  const fetchApiKeys = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/api-keys/', {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data);
      } else {
        toast({
          title: 'Error fetching API keys',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error fetching API keys',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleAdd = () => {
    setSelectedKey(null);
    setFormData({
      name: '',
      description: '',
      is_active: true,
    });
    onOpen();
  };

  const handleEdit = (key) => {
    setSelectedKey(key);
    setFormData({
      name: key.name || '',
      description: key.description || '',
      is_active: key.is_active !== false,
    });
    onOpen();
  };

  const handleDelete = async (key) => {
    if (!window.confirm(`Are you sure you want to delete API key "${key.name}"?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const response = await fetch(`/api/v1/api-keys/${key.id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
      });
      if (response.ok) {
        toast({
          title: 'API key deleted',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchApiKeys();
      } else {
        toast({
          title: 'Error deleting API key',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error deleting API key',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const csrftoken = getCookie('csrftoken');
      const url = selectedKey
        ? `/api/v1/api-keys/${selectedKey.id}/`
        : '/api/v1/api-keys/';
      const method = selectedKey ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Token ${token}`,
          'X-CSRFToken': csrftoken,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: `API key ${selectedKey ? 'updated' : 'created'}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onClose();
        setSelectedKey(null);
        fetchApiKeys();
      } else {
        const errorData = await response.json();
        toast({
          title: 'Error saving API key',
          description: JSON.stringify(errorData),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error saving API key',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const copyToClipboard = (key) => {
    navigator.clipboard.writeText(key);
    toast({
      title: 'Copied to clipboard',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  if (loading) {
    return (
      <Center h="200px">
        <Spinner size="xl" color="purple.500" thickness="4px" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="gray.800">API Keys</Heading>
          <Text color="gray.500" mt={1}>Manage API keys for external access</Text>
        </Box>
        <Button
          leftIcon={<FaPlus />}
          bgGradient="linear(to-r, purple.500, purple.600)"
          color="white"
          _hover={{
            bgGradient: "linear(to-r, purple.600, purple.700)",
            transform: "translateY(-2px)",
            boxShadow: "lg",
          }}
          onClick={handleAdd}
          borderRadius="lg"
          px={6}
        >
          Add API Key
        </Button>
      </Flex>

      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        borderWidth="1px"
        borderColor="gray.100"
        overflow="hidden"
      >
        <Table variant="simple">
          <Thead bg="gray.50">
            <Tr>
              <Th color="gray.600" py={4}>Name</Th>
              <Th color="gray.600">Key</Th>
              <Th color="gray.600">Description</Th>
              <Th color="gray.600">Status</Th>
              <Th color="gray.600">Created</Th>
              <Th color="gray.600" textAlign="right">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {apiKeys.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center" py={8} color="gray.500">
                  No API keys found. Click "Add API Key" to create one.
                </Td>
              </Tr>
            ) : (
              apiKeys.map((key) => (
                <Tr
                  key={key.id}
                  _hover={{ bg: "gray.50" }}
                  transition="background 0.2s"
                >
                  <Td>
                    <HStack>
                      <FaKey color="#805AD5" />
                      <Text fontWeight="medium" color="gray.800">{key.name}</Text>
                    </HStack>
                  </Td>
                  <Td>
                    <HStack>
                      <Code fontSize="sm" colorScheme="gray">
                        {key.key.substring(0, 8)}...{key.key.substring(key.key.length - 4)}
                      </Code>
                      <IconButton
                        aria-label="Copy key"
                        icon={<FaCopy />}
                        size="xs"
                        variant="ghost"
                        colorScheme="purple"
                        onClick={() => copyToClipboard(key.key)}
                      />
                    </HStack>
                  </Td>
                  <Td color="gray.600" maxW="200px" isTruncated>
                    {key.description || '-'}
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={key.is_active ? 'green' : 'red'}
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {key.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td fontSize="sm" color="gray.600">
                    {new Date(key.created_at).toLocaleDateString()}
                  </Td>
                  <Td textAlign="right">
                    <HStack spacing={2} justify="flex-end">
                      <IconButton
                        aria-label="Edit API key"
                        icon={<FaEdit />}
                        size="sm"
                        variant="ghost"
                        colorScheme="purple"
                        onClick={() => handleEdit(key)}
                      />
                      <IconButton
                        aria-label="Delete API key"
                        icon={<FaTrash />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => handleDelete(key)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* API Key Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader borderBottomWidth="1px" py={4}>
            {selectedKey ? 'Edit' : 'Add New'} API Key
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel fontWeight="medium">Name</FormLabel>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  borderRadius="lg"
                  focusBorderColor="purple.500"
                  placeholder="e.g., Production API Key"
                />
              </FormControl>
              <FormControl>
                <FormLabel fontWeight="medium">Description</FormLabel>
                <Input
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  borderRadius="lg"
                  focusBorderColor="purple.500"
                  placeholder="What is this key used for?"
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel fontWeight="medium" mb={0}>Active</FormLabel>
                <Switch
                  name="is_active"
                  isChecked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  colorScheme="purple"
                />
              </FormControl>
              {!selectedKey && (
                <FormHelperText>
                  A new UUID-based API key will be automatically generated.
                </FormHelperText>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" py={4}>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button
              bgGradient="linear(to-r, purple.500, purple.600)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, purple.600, purple.700)",
              }}
              onClick={handleSave}
            >
              {selectedKey ? 'Save Changes' : 'Create API Key'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ApiKeys;
